<script setup lang="ts">
import { useSessionIndividual } from "~/composables/sessions/useSessionIndividual";
import { useSessions } from "~/composables/sessions/useSessions";
import { useFormats } from "~/composables/utils/useFormat";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { useTrainings } from "~/composables/trainings/useTrainings";
import { TrainingTypeEnum } from "~/utils/enums/training.enums";
import { SessionItemsFormats } from "~/utils/options/session-items.options";
import LocationUrl from "~/components/Pages/training-plan/session-details/tabs/general/LocationUrl.vue";
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import { useSalaryCostsAccess } from "~/composables/companies/useSalaryCostsAccess";
import { DEFAULT_TIMEZONE } from "~/utils/helpers/timezones";
import { formatTimezoneLabel } from "~/utils/helpers/timezone-labels";
import { useAuthStore } from "~/store/auth/auth.store";
import { useSessionEvaluations } from "~/composables/evaluations/useSessionEvaluations";
import { GlobalEvaluationsTabEnum } from "~/utils/enums/evaluations.enums";
import { interleaveSessionSteps } from "~/utils/helpers/sessions/session-steps";
import type { TrainingPlanIndividualTimeslots } from "@simbelapp/order-sdk";

defineProps({
  split: {
    type: Boolean,
    default: false,
  },
});

const format = useFormats();
const session = useSessions();
const { trainingTypeMap } = useTrainings();
const sessionIndividual = useSessionIndividual();
const { canSeeSalaryCosts } = useSalaryCostsAccess();

const data = computed(() => sessionIndividual.getSessionRecap());

const authStore = useAuthStore();
const { formatDate } = useSessionEvaluations();

const steps = computed(() =>
  interleaveSessionSteps(
    data.value.timeslots ?? [],
    (timeslot) => timeslot.startDate,
    data.value.assessments,
    (questionnaire) => questionnaire.date,
  ),
);

/** Same rule as the session page: the step's own zone, never the reader's, on every dated step. */
function timezoneLabel(timeslot: TrainingPlanIndividualTimeslots): string | null {
  if (!timeslot.startDate || !timeslot.endDate) return null;

  return formatTimezoneLabel(timeslot.timezone ?? DEFAULT_TIMEZONE, timeslot.startDate, authStore.user?.language);
}

/** A step without hours shows its dates alone, with no « à » left hanging. */
function timeslotDates(timeslot: TrainingPlanIndividualTimeslots): string {
  const at = (time?: string) => (time ? ` à ${time}` : "");
  const start = `${format.toDateDisplay(timeslot.startDate)}${at(timeslot.startTime)}`;
  const end = `${format.toDateDisplay(timeslot.endDate)}${at(timeslot.endTime)}`;

  return `du ${start} au ${end}`;
}

// Hidden outright — not zeroed — for a group admin the company hides loaded hourly costs from.
const salaryCost = computed(() => (canSeeSalaryCosts.value ? Number(data.value.salaryCost) : 0));

// Deep-links this participant's own à chaud satisfaction survey — or questionnaire, on its tab — on the global
// Évaluations page. New tab (like SessionSatisfactionsRedirect) so the inscription panel stays open behind it.
function openUserEvaluation(recipientId?: string | null, tab?: GlobalEvaluationsTabEnum) {
  if (!recipientId) return;
  const query = `recipientId=${encodeURIComponent(recipientId)}${tab ? `&tab=${tab}` : ""}`;
  window.open(`/evaluations?${query}`, "_blank", "noopener,noreferrer");
}

const sessionTypeMapped = () => {
  return (
    session.sessionTypeMapping(data.value.type as string) ?? {
      value: "À définir",
      icon: "/icons/generic/tooltip.svg",
    }
  );
};

const financingTotal = computed<number | null>(() => {
  if (!data.value.financing.length) return null;
  return data.value.financing.reduce((sum, item) => sum + item.amount, 0);
});

const totalPriceExternalCosts = computed(() => {
  return data.value?.otherCosts?.reduce((acc, item) => {
    return acc + Number(item.priceExclTax);
  }, 0);
});

const locationSegments = ref<Array<string | { type: "link"; url: string }>>([]);

onMounted(() => {
  if (data.value?.location) {
    locationSegments.value = parseLocationText(data.value.location);
  }
});

function parseLocationText(text: string) {
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
  const segments: Array<string | { type: "link"; url: string }> = [];

  const parts = text.split(urlRegex);

  parts.forEach((part) => {
    if (urlRegex.test(part)) {
      if (part.startsWith("www.")) {
        part = "http://" + part;
      }
      segments.push({ type: "link", url: part });
    } else {
      segments.push(part);
    }
  });

  return segments;
}
</script>

<template>
  <div class="session_recap__container">
    <h3 class="heading_3_demi text-primary-color p-[8px]">Récapitulatif</h3>
    <div v-if="data" class="session_recap__grid mt-[16px]" :data-split="split">
      <div class="session_recap__split">
        <section>
          <div class="session_recap__key">
            <img src="/icons/emojis/trombone.svg" alt="" />
            <h5>Type</h5>
          </div>
          <div v-if="data.trainingType === TrainingTypeEnum.INTERNAL" class="tag_main tag_grey gap-[4px] items-center">
            <SvgIcon
              :name="trainingTypeMap(TrainingTypeEnum.INTERNAL)?.icon!"
              :style="{ height: '16px', width: '16px' }"
            />
            <span class="text-secondary-color body_regular text-center">{{
              trainingTypeMap(TrainingTypeEnum.INTERNAL)?.value
            }}</span>
          </div>
          <div v-else class="tag_main tag_grey gap-[4px] items-center">
            <img
              v-if="sessionTypeMapped()"
              :src="sessionTypeMapped()?.icon"
              alt=""
              :style="{ height: '16px', width: '16px' }"
            />
            <span class="text-secondary-color body_regular text-center">{{ sessionTypeMapped()?.value }}</span>
          </div>
        </section>
      </div>

      <div class="session_recap__split">
        <section>
          <div class="session_recap__key">
            <img src="/icons/emojis/argent.svg" alt="" />
            <h5>Prix HT</h5>
          </div>
          <p>
            {{
              financingTotal !== null
                ? `${format.toCurrency(financingTotal)} par participant`
                : data.priceExclTax
                  ? `${format.toCurrency(Number(data.priceExclTax))} par participant`
                  : "-"
            }}
          </p>
        </section>
        <section class="items-start">
          <div class="session_recap__key">
            <img src="/icons/emojis/puzzle.svg" alt="" />
            <h5>Financement</h5>
          </div>
          <div v-if="data.financing.length" class="flex flex-row">
            <div class="session_recap_financement">
              <p v-for="item in data.financing" :key="item.name">
                <span>{{ format.toCurrency(item.amount) }}</span>
              </p>
            </div>
            <div class="session_recap_financement !items-start">
              <p v-for="item in data.financing" :key="item.name">
                <span>{{ item.name }}</span>
              </p>
            </div>
          </div>
          <div v-else>
            <p>-</p>
          </div>
        </section>
        <section class="items-start">
          <div class="session_recap__key">
            <img src="/icons/emojis/balance.svg" alt="" />
            <h5>Autres coûts</h5>
          </div>
          <div v-if="data.otherCosts?.length || salaryCost" class="flex flex-row">
            <div class="session_recap_financement">
              <div v-if="data.otherCosts?.length" class="flex gap-2">
                <span>{{ format.toCurrency(Number(totalPriceExternalCosts)) }}</span>
                <span>Frais annexes</span>
              </div>
              <div v-if="salaryCost" class="flex gap-2">
                <span>{{ format.toCurrency(salaryCost) }}</span>
                <span>Coûts salaires participants</span>
              </div>
            </div>
          </div>
          <div v-else>
            <p>-</p>
          </div>
        </section>

        <section>
          <div class="session_recap__key">
            <img src="/icons/emojis/main_OK.svg" alt="" />
            <h5>Satisfaction</h5>
          </div>
          <div class="flex items-center gap-2">
            <p>{{ data?.satisfaction ?? 0 }} / 10</p>
            <AppButton
              v-if="data?.recipientId"
              type="link"
              icon="link"
              label="Voir l'évaluation"
              @click="openUserEvaluation(data.recipientId)"
            />
          </div>
        </section>
        <section>
          <div class="session_recap__key">
            <img src="/icons/emojis/note.svg" alt="" />
            <h5>Score</h5>
          </div>
          <p v-if="Number.isFinite(data?.score)">{{ data?.score }}%</p>
          <p v-else>-</p>
        </section>
      </div>
    </div>
    <div v-if="steps.length" class="mb-[16px]">
      <h3 class="heading_3_demi text-primary-color p-[8px]">Étapes</h3>
      <template v-for="(step, stepIndex) in steps" :key="stepIndex">
        <div v-if="step.questionnaire" class="session_recap__grid">
          <span class="text-primary-color mb-2">QUESTIONNAIRE - {{ step.questionnaire.templateName }}</span>
          <section>
            <div class="session_recap__key">
              <img src="/icons/emojis/calendrier.svg" alt="" />
              <h5>Date</h5>
            </div>
            <span class="body_regular">Le {{ formatDate(step.questionnaire.date) }}</span>
          </section>
          <section>
            <div class="session_recap__key">
              <img src="/icons/emojis/note.svg" alt="" />
              <h5>Score</h5>
            </div>
            <div class="flex items-center gap-2">
              <span class="body_regular">{{ format.toScoreDisplay(step.questionnaire.score) }}</span>
              <AppButton
                v-if="step.questionnaire.canViewEvaluation"
                type="link"
                icon="link"
                label="Voir le questionnaire"
                @click="openUserEvaluation(step.questionnaire.recipientId, GlobalEvaluationsTabEnum.QUESTIONNAIRES)"
              />
            </div>
          </section>
        </div>
        <div v-else-if="step.timeslot" class="flex flex-col gap-4">
          <!-- A one-item loop: it names the entry's timeslot for the markup below. -->
          <!-- prettier-ignore -->
          <div v-for="timeslot in [step.timeslot]" :key="timeslot.timeslotId" class="session_recap__grid">
          <span v-if="data.timeslots && data.timeslots?.length > 1 && timeslot.name" class="text-primary-color mb-2">ÉTAPE
            {{ step.timeslotIndex + 1 }} -
            {{ timeslot.name }}</span>
          <span v-else-if="data.timeslots && data.timeslots?.length > 1" class="text-primary-color mb-2">ÉTAPE {{
            step.timeslotIndex + 1 }}</span>
          <section>
            <!-- Top-aligned, so « Dates » stays on the date's line when the zone adds one below it. -->
            <div class="flex flex-row items-start">
              <div class="session_recap__key">
                <img src="/icons/emojis/calendrier.svg" alt="" />
                <h5>Dates</h5>
              </div>
              <div>
                <span v-if="timeslot?.startDate && timeslot?.endDate" class="body_regular break-words">{{
                  timeslotDates(timeslot)
                }}</span>
                <span v-else>-</span>
                <div v-if="timezoneLabel(timeslot)" class="notranslate small_regular leading-5 text-secondary-color mt-2">
                  {{ timezoneLabel(timeslot) }}
                </div>
              </div>
            </div>
          </section>
          <section>
            <div class="flex flex-row">
              <div class="session_recap__key">
                <img src="/icons/emojis/livres.svg" alt="" />
                <h5>Format</h5>
              </div>
              <div class="body_regular break-words">
                <div>
                  <span v-if="timeslot?.format" class="body_regular break-words">{{
                    SessionItemsFormats.find((x) => x.name === timeslot?.format)?.value
                  }}</span>
                  <span v-else>-</span>
                </div>
              </div>
            </div>
          </section>
          <section>
            <div class="flex flex-row items-start">
              <div class="session_recap__key">
                <img src="/icons/emojis/pin.svg" alt="" />
                <h5>Lieu</h5>
              </div>
              <div class="body_regular locationUrl">
                <div v-if="timeslot.selectedLocation" class="flex flex-col gap-1">
                  <div class="flex gap-2">
                    <span class="body_regular">
                      {{ timeslot.selectedLocation?.locationName }}
                      <template v-if="timeslot.selectedLocation?.locationManualAddress">
                        - {{ timeslot.selectedLocation?.locationManualAddress }}
                      </template>
                      <template v-if="timeslot.selectedLocation?.locationAccess">
                        - <a
:href="timeslot.selectedLocation?.locationAccess" target="_blank"
                          class="text-secondary-color underline">
                          Voir le plan d’accès
                        </a>
                      </template>
                    </span>
                  </div>
                  <span v-if="timeslot.selectedRoom" class="body_regular">
                    {{ timeslot.selectedRoom?.roomName }}
                    <template v-if="timeslot.selectedRoom?.roomAccess">
                      - {{ timeslot.selectedRoom?.roomAccess }}
                    </template>
                    <template v-if="timeslot.selectedRoom?.roomEquipment">
                      - {{ timeslot.selectedRoom?.roomEquipment }}
                    </template>
                  </span>
                </div>
                <LocationUrl v-else-if="timeslot.location" :text="timeslot.location"></LocationUrl>
                <span v-else>-</span>
              </div>
            </div>
          </section>
          <section>
            <div class="flex flex-row">
              <div class="session_recap__key">
                <img src="/icons/emojis/finish.svg" alt="" />
                <h5>Durée</h5>
              </div>
              <div class="body_regular break-words">
                <div>
                  <span class="body_regular break-words">{{ Number.isFinite(timeslot?.attendedHours) ?
                    format.formatTo2Decimals(Number(timeslot?.attendedHours)) : "-" }}h</span>
                  / <span>{{ format.formatTo2Decimals(Number(timeslot?.trainingDurationHours)) }}h</span> &nbsp;<span
                    v-if="Number.isFinite(timeslot?.attendedHours)" class="body_regular break-words">({{
                      format.toPercentageDisplay(
                        format.formatTo2Decimals(
                          Number(timeslot?.attendedHours) / Number(timeslot?.trainingDurationHours),
                        ) * 100,
                      )
                    }})</span>
                </div>

              </div>
            </div>
          </section>
          <section>
            <div class="flex flex-row">
              <div class="session_recap__key">
                <img src="/icons/emojis/trainer.svg" alt="" />
                <h5>Formateur</h5>
              </div>
              <div class="body_regular break-words">
                <div class="body_regular break-words">
                  <div v-if="timeslot?.internalTrainers?.length" class="body_regular break-words">
                    <span v-for="(trainer, index) in timeslot?.internalTrainers" :key="trainer.userId">
                      {{ trainer.displayValue }}<span v-if="index < timeslot?.internalTrainers?.length - 1">,
                      </span>
                    </span>
                  </div>
                  <span v-else>-</span>
                </div>

              </div>
            </div>
          </section>
        </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.session_recap_financement {
  @apply flex flex-col items-start mr-[8px];
}

.session_recap__container {
  @apply pt-[8px];
  border-bottom: 1px solid var(--grey-lightest);
}

.session_recap__grid {
  @apply flex flex-col gap-[8px] px-[8px] py-[16px] mb-[16px] mt-[8px];
  background: var(--grey-light);
  border-radius: 8px;
  border: 1px solid var(--grey-lightest);

  &[data-split="true"] {
    @apply flex-row;
  }

  .session_recap__split {
    @apply grid gap-[8px] flex-1;
  }

  section {
    @apply inline-flex px-[8px];

    .session_recap__key {
      @apply inline-flex items-center min-w-[112px] mr-[8px] text-secondary-color;

      img {
        @apply w-[16px] h-[16px] mr-[4px];
      }
    }
  }

  .tag_main {
    display: inline-flex;
    padding: 4px 8px;
    border-radius: 8px;
  }
}

.locationUrl {
  word-break: break-word;
  overflow-wrap: break-word;
}
</style>
